/** Turning a string into rectangles, once, for both renderers.
 *
 * There is one glyph table (`glyphs.ts`) and one layout (this file). The Pixi
 * renderer and the DOM renderer both consume the runs this produces, so a
 * numeral on the battlefield and the same numeral in a React panel are the same
 * shape by construction. Two hand-maintained faces would drift within a round.
 *
 * The output is **run-length encoded per row**: a row of five lit pixels is one
 * rectangle, not five. That matters because both back-ends pay per rectangle -
 * Pixi builds geometry, the DOM builds path data - and a four-digit count would
 * otherwise be about 120 primitives.
 *
 * Nothing here touches a DOM, a canvas or Pixi, which is what lets the tests
 * assert the geometry directly.
 *
 * ## The keyline is OFF unless a label asks for it
 *
 * This file used to dilate a one-pixel dark halo around all eight neighbours of
 * every lit pixel, unconditionally, for every string in the build. Measured on
 * the layout itself: `227987` is 72 pixels of body against **187 of keyline,
 * 2.60x**; `20,000` is 2.37x; `42` is 2.52x; and across all six strings checked
 * there was not one background column left anywhere in the box, because a
 * four-column `0` has a two-column counter that a one-pixel inward dilation
 * closes from both walls, and `TRACKING` is 1 with the keyline eating a column
 * from each side of the gap. The classic at native 800x600 measures 29% ink against
 * 71% plate on `Month:` and 17% on a gold `14700`; we were three to five times
 * denser, and a number that dense stops reading as letters on a ground and
 * starts reading as a black slab with light scratches in it.
 *
 * A keyline is what you draw a string with when you do not control what is
 * behind it - a damage floater over the battlefield, a stack count printed onto
 * a creature. Everything else in this build sits on a plate the build itself
 * painted, which is exactly the case the classic sets without an outline.
 *
 * `outlineAlpha: 0` was not the fix and is why this is a metric argument rather
 * than a colour one: a transparent keyline still reserved its two rows and two
 * columns in `LINE_H` and in the measured width, which is what crowded `42`
 * against its plaque border while being invisible on the near-black plate.
 */

import type { FaceId } from "./glyphs.js";
import { GLYPH_H, OUTLINE, descentOf, descentOfText, faceOf, glyphFor } from "./glyphs.js";

/**
 * How a string is set: which of the three faces, and whether it carries a
 * keyline. Both default, and the defaults are the HUD face with no keyline.
 */
export interface BitmapTextStyle {
  readonly face?: FaceId | undefined;
  /**
   * Glyph pixels of dark dilated around the body on all eight sides. `0` - the
   * default - draws none and reserves none. `OUTLINE` (1) is the only other
   * value the face is drawn for.
   */
  readonly keyline?: number | undefined;
}

/** Glyph pixels of keyline `style` asks for. Zero unless it says otherwise. */
export function keylineOf(style?: BitmapTextStyle): number {
  const k = style?.keyline ?? 0;
  return k > 0 ? Math.round(k) : 0;
}

/**
 * The line box: the body of the face plus whatever keyline it asked for.
 *
 * This is the height a call site lays out against, and it does NOT move when a
 * string gains a descender. A comma is two rows of ink below the baseline; if
 * those rows were metric, `20,000` would be a taller line than `20` and the two
 * would stop sharing a centre in the resource bar they sit in side by side.
 * Descenders overflow the line box, the way they do in any real face - so the
 * drawn box (`BitmapTextLayout.height`) can be taller than this, and the
 * renderers position on the line height while drawing the whole thing.
 */
export function lineHeightOf(style?: BitmapTextStyle): number {
  return faceOf(style?.face).capH + keylineOf(style) * 2;
}

/**
 * The line box of the default setting: the HUD face, no keyline.
 *
 * It was `GLYPH_H + OUTLINE * 2` when every label was keylined. It is the body
 * alone now, because the default label has no keyline to reserve. A call site
 * that sets a different face or turns the keyline on must ask `lineHeightOf`.
 */
export const LINE_H = GLYPH_H;

/** One horizontal run of pixels, `h` = 1 row, in glyph-pixel units. */
export interface BitmapRun {
  readonly x: number;
  readonly y: number;
  readonly w: number;
}

export interface BitmapTextLayout {
  readonly text: string;
  /** Box width in glyph pixels, keyline included. */
  readonly width: number;
  /**
   * Drawn height in glyph pixels, keyline included: the line box, plus the
   * deepest descender in the string. Position on the line box, not on this.
   */
  readonly height: number;
  /** The line box: body plus keyline, without the descender. */
  readonly lineHeight: number;
  /** Glyph pixels of ink below the line box. 0 unless the string descends. */
  readonly descent: number;
  /** The lit body of the glyphs. */
  readonly fill: readonly BitmapRun[];
  /** The 1px dark keyline around that body, with the body itself removed. Empty
   * unless the style asked for one. */
  readonly outline: readonly BitmapRun[];
}

/** Box size of `text` in glyph pixels, keyline included. Cheap; no runs built. */
export function measureBitmapText(
  text: string, style?: BitmapTextStyle,
): { readonly width: number; readonly height: number; readonly descent: number } {
  const face = faceOf(style?.face);
  const key = keylineOf(style);
  let w = 0;
  for (let i = 0; i < text.length; i++) {
    if (i > 0) w += face.tracking;
    w += glyphFor(text.charAt(i), style?.face).w;
  }
  const descent = descentOfText(text, style?.face);
  return { width: w + key * 2, height: lineHeightOf(style) + descent, descent };
}

/**
 * `text` as two sets of rectangles: the glyph bodies, and - only if the style
 * asked for one - the keyline around them.
 *
 * When it is asked for, the keyline is a full 8-neighbour dilation rather than a
 * drop shadow, because a floater has to survive being drawn over a bright
 * sprite *and* over near-black wood, and a shadow only ever solves one of
 * those. That is also the reason it costs what it costs, and the reason a
 * string on a plate should not be paying it.
 */
export function layoutBitmapText(text: string, style?: BitmapTextStyle): BitmapTextLayout {
  const { width, height, descent } = measureBitmapText(text, style);
  const face = faceOf(style?.face);
  const key = keylineOf(style);
  // One byte per cell of the whole box, keyline margin included, so the
  // dilation below cannot run off the edge of the grid it writes into.
  const body = new Uint8Array(width * height);

  let pen = key;
  for (let i = 0; i < text.length; i++) {
    const glyph = glyphFor(text.charAt(i), style?.face);
    const rows = face.capH + descentOf(glyph);
    for (let row = 0; row < rows; row++) {
      const bits = glyph.rows[row] ?? 0;
      for (let col = 0; col < glyph.w; col++) {
        if ((bits & (1 << (glyph.w - 1 - col))) === 0) continue;
        body[(row + key) * width + pen + col] = 1;
      }
    }
    pen += glyph.w + face.tracking;
  }

  const halo = new Uint8Array(width * height);
  if (key > 0) {
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        if (body[y * width + x] !== 1) continue;
        for (let dy = -key; dy <= key; dy++) {
          for (let dx = -key; dx <= key; dx++) {
            const ny = y + dy, nx = x + dx;
            if (ny < 0 || ny >= height || nx < 0 || nx >= width) continue;
            if (body[ny * width + nx] === 1) continue;
            halo[ny * width + nx] = 1;
          }
        }
      }
    }
  }

  return {
    text,
    width,
    height,
    lineHeight: lineHeightOf(style),
    descent,
    fill: runsOf(body, width, height),
    outline: key > 0 ? runsOf(halo, width, height) : [],
  };
}

/** Row-major run-length encoding of a 0/1 grid. */
function runsOf(grid: Uint8Array, width: number, height: number): readonly BitmapRun[] {
  const out: BitmapRun[] = [];
  for (let y = 0; y < height; y++) {
    let x = 0;
    while (x < width) {
      if (grid[y * width + x] !== 1) { x++; continue; }
      const start = x;
      while (x < width && grid[y * width + x] === 1) x++;
      out.push({ x: start, y, w: x - start });
    }
  }
  return out;
}

/** Where a box of `size` sits when anchored at `anchor` (0 = near, 1 = far). */
export function anchorOffset(size: number, anchor: number): number {
  return Math.round(-size * anchor);
}

/** Re-exported so a call site can name a keyline width without a magic 1. */
export { OUTLINE as KEYLINE };
